import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { IOrchardFormValues } from '../../../../../@types/model/masterData/orchard/orchardFormValues';
import { useAppSelector } from '../../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect, ControlledDateInput } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { LocalizationProvider } from '@mui/x-date-pickers';
import CustomTooltip from '../../../../../components/tooltip/tooltip';
import { yupResolver } from '@hookform/resolvers/yup';
import OrchardModelHelper from '../../../../../@types/model/masterData/orchard/orchardModelHelper';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';

interface IOrchardFormProps {
    isLoading : boolean;
    initialValues : IOrchardFormValues;
    onSubmit : (values : IOrchardFormValues) => void;
    onClose : () => void;
    isImported : boolean;
}

const OrchardForm = (props : IOrchardFormProps) : React.ReactElement => {
    const commodities = useAppSelector(x => x.masterData.commodities);
    const farms = useAppSelector(x => x.masterData.farms);
    const varieties = useAppSelector(x => x.masterData.varieties);
    const departments = useAppSelector(x => x.masterData.departments);
    const projects = useAppSelector(x => x.masterData.projects);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);

    const { handleSubmit, control, formState: { isValid, isDirty }, watch, trigger } = useForm<IOrchardFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(OrchardModelHelper.formSchema()),
        mode: 'onChange',
    });

    const selectedCommodityOption = watch('commodity');

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities
            .filter(x => x.isActive && x.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y)))
            .map((x) => {
                return { label: `(${x.code}) ${x.name}`, value: x.id };
            });
    }, [commodities, selectedOrganizationIds]);

    const farmOptions = useMemo(() => {
        return farms
            .filter(x => x.isActive && x.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y)))
            .map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id }));
    }, [farms, selectedOrganizationIds]);

    const varietyOptions = useMemo(() => {
        if (!selectedCommodityOption) return [];
        return varieties
            .filter(x => x.commodityId === selectedCommodityOption.value && x.isActive)
            .map((x) => {
                return { label: `(${x.code}) ${x.name}`, value: x.id };
            });
    }, [varieties, selectedCommodityOption]);

    const departmentOptions = useMemo(() => {
        return departments.filter(x => x.isActive)
            .map((x) => {
                return { label: `(${x.code}) ${x.shortDescription}`, value: x.id };
            });
    }, [departments]);

    const projectOptions = useMemo(() => {
        if (!selectedCommodityOption) return [];
        return projects
            .filter(x => x.isActive && x.commodityId === selectedCommodityOption.value)
            .map((x) => {
                return { label: `(${x.number}) ${x.description}`, value: x.id };
            });
    }, [projects, selectedCommodityOption]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    const tooltipTitle = props.isImported ? 'Cannot change this field if orchard is imported' : '';

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <div className={'fdr mb15'}>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <ControlledTextField
                                name={'code'}
                                label={'Code'}
                                control={control}
                                defaultValue={props.initialValues.code}
                                disabled={props.isImported}
                                className={'flx1'}
                                size={'small'}
                            />
                        </CustomTooltip>
                        <div className={'w20'}/>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <ControlledTextField
                                name={'name'}
                                label={'Name'}
                                control={control}
                                defaultValue={props.initialValues.name}
                                disabled={props.isImported}
                                className={'flx1'}
                                size={'small'}
                            />
                        </CustomTooltip>
                    </div>
                    <div className={'fdr mb15'}>
                        <ControlledTextField
                            name={'subBlock'}
                            label={'Visual Identifier'}
                            control={control}
                            defaultValue={props.initialValues.subBlock}
                            className={'flx1'}
                            size={'small'}
                        />
                        <div className={'w20'}/>
                        <ControlledTextField
                            name={'complianceCode'}
                            label={'Compliance Code'}
                            control={control}
                            defaultValue={props.initialValues.complianceCode}
                            className={'flx1'}
                            size={'small'}
                        />
                    </div>
                    <div className={'fdr mb15'}>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <ControlledTextField
                                className={'flx1'}
                                name={'plantedHectares'}
                                label={'Planted Hectares'}
                                control={control}
                                type={'number'}
                                defaultValue={props.initialValues.plantedHectares}
                                disabled={props.isImported}
                                size={'small'}
                            />
                        </CustomTooltip>
                        <div className={'w20'}/>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <ControlledDateInput
                                    name={'endedOn'}
                                    label={'Ended On'}
                                    control={control}
                                    defaultValue={props.initialValues.endedOn ?? undefined}
                                    disabled={props.isImported}
                                    className={'flx1'}
                                    size={'small'}
                                    clearable
                                />
                            </LocalizationProvider>
                        </CustomTooltip>
                    </div>
                    <div className={'fdr mb10'}>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <ControlledMuiAutoCompleteSelect
                                name={'commodity'}
                                label={'Commodity'}
                                control={control}
                                options={commodityOptions}
                                defaultValues={props.initialValues.commodity}
                                disabled={props.isImported}
                            />
                        </CustomTooltip>
                        <div className={'w20'}/>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <ControlledMuiAutoCompleteSelect
                                name={'defaultVariety'}
                                label={'Default Variety'}
                                control={control}
                                options={varietyOptions}
                                defaultValues={props.initialValues.defaultVariety}
                                disabled={props.isImported}
                            />
                        </CustomTooltip>
                    </div>
                    <div className={'fdr mb10'}>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <ControlledMuiAutoCompleteSelect
                                name={'farm'}
                                label={'Farm'}
                                control={control}
                                options={farmOptions}
                                defaultValues={props.initialValues.farm}
                                disabled={props.isImported}
                            />
                        </CustomTooltip>
                        <div className={'w20'}/>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <ControlledMuiAutoCompleteSelect
                                name={'project'}
                                label={'Project Number'}
                                control={control}
                                options={projectOptions}
                                defaultValues={props.initialValues.project}
                                disabled={props.isImported}
                            />
                        </CustomTooltip>
                    </div>
                    <div className={'fdr mb10'}>
                        <CustomTooltip className={'flx1'} title={tooltipTitle}>
                            <ControlledMuiAutoCompleteSelect
                                name={'department'}
                                label={'Department'}
                                control={control}
                                options={departmentOptions}
                                defaultValues={props.initialValues.department}
                                disabled={props.isImported}
                            />
                        </CustomTooltip>
                        <div className={'w20'}/>
                    </div>
                    <CustomTooltip title={tooltipTitle}>
                        <div className={'flx1 fdr jcfs'}>
                            <ControlledLabelledCheckBox
                                name={'isActive'}
                                label={'Is Active'}
                                control={control}
                                defaultValue={props.initialValues.isActive}
                                disabled={props.isImported}
                            />
                        </div>
                    </CustomTooltip>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button
                            className={'cpd mr20'} type={'button'} onClick={props.onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={!isDirty || !isValid || props.isLoading}
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default OrchardForm;
